import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import Select, { OptionProps } from 'components/Select/Select'
import TransactionConfirmationModal from 'components/TransactionConfirmationModal'
import { useTranslation } from 'contexts/Localization'
import { usePairs } from 'hooks/usePairs'
import useToast from 'hooks/useToast'
import useTotalSupply from 'hooks/useTotalSupply'
import React, { useState, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router'
import { useBurnState, useFetchedPairRedemptions, useRemovalOrders } from 'state/burn/hooks'
import { PairRedemptionResponse } from 'state/burn/queries/types'
import { useDeserializedFarms } from 'state/farms/useDeserializedFarms'
import { useActiveFarmsUserDataFetching, useFinishedFarmsUserDataFetching } from 'state/farms/useDeserializedFarmsUser'
import { useTransactionAdder } from 'state/transactions/hooks'
import { DeserializedRemovalOrder, DeserializedRemovalOrderUserData } from 'state/types'
import { toV2LiquidityToken, useTrackedTokenPairs } from 'state/user/hooks'
import { useTokenBalancesWithLoadingIndicator } from 'state/wallet/hooks'
import { getAddress } from 'utils/addressHelpers'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import { unwrappedToken } from 'utils/wrappedCurrency'
import HistoryCard from 'views/Pool/components/HistoryCard'
import RemovalOrderCard from 'views/Pool/components/RemovalOrderCard'
import SkeletonCard from 'views/Pool/components/SkeletonCard'
import SuppliedCard from 'views/Pool/components/SuppliedCard'
import { CardLayout } from 'views/Pools'
import DuplicatedOrderWarningModal from 'views/RemoveLiquidity/components/DuplicatedOrderWarningModal'
import useClpHandlers from 'views/RemoveLiquidity/hooks/useClpHandlers'
import { Pair, TokenAmount } from 'vvs-sdk'
import { Box, Text, useMatchBreakpoints, useModal } from 'vvs-uikit'

type LiquidityCardType = 'supplied' | 'orders' | 'history'

const cardComponents = {
  history: HistoryCard,
  orders: RemovalOrderCard,
  supplied: SuppliedCard,
}

export default function MyLiquidity() {
  const { isMobile } = useMatchBreakpoints()
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const history = useHistory()

  const { toastError } = useToast()
  const [attemptingTxn] = useState(true)
  const [attemptingModalCancelTxn, setAttemptingModalCancelTxn] = useState(false)
  const [txHash, setTxHash] = useState<string>('')
  const [currentPair, setCurrentPair] = useState<Pair>()
  const [currentOrder, setCurrentOrder] = useState<DeserializedRemovalOrder>()

  const [filter, setFilter] = useState('all')

  const farms = useDeserializedFarms((s) => s.value.farms)

  // fetch for APR
  useActiveFarmsUserDataFetching()
  useFinishedFarmsUserDataFetching()

  // fetch the user's balances of all tracked V2 LP tokens
  const trackedTokenPairs = useTrackedTokenPairs()
  const tokenPairsWithLiquidityTokens = useMemo(
    () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
    [trackedTokenPairs],
  )
  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
    [tokenPairsWithLiquidityTokens],
  )
  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens,
  )

  // fetch the reserves for all V2 pools in which the user has a balance
  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairsBalances[liquidityToken.address]?.greaterThan('0'),
      ),
    [tokenPairsWithLiquidityTokens, v2PairsBalances],
  )

  const [data, , loading] = useFetchedPairRedemptions(account)

  const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const allV2Pairs = usePairs(tokenPairsWithLiquidityTokens.map(({ tokens }) => tokens))
  const v2IsLoading =
    fetchingV2PairBalances || v2Pairs?.length < liquidityTokensWithBalances.length || v2Pairs?.some((V2Pair) => !V2Pair)

  const allV2PairsWithLiquidity = v2Pairs
    .map(([, pair]) => pair)
    .filter((v2Pair): v2Pair is Pair => Boolean(v2Pair))
    .map((pair) => ({
      type: 'supplied' as LiquidityCardType,
      pair,
      lpAddress: pair.liquidityToken.address,
      lpTokenValue: new BigNumber(v2PairsBalances[pair.liquidityToken.address].toSignificant()).times(
        getLpUsdcPrice(farms.find((f) => getAddress(f.lpAddresses) === pair.liquidityToken.address)),
      ),
    }))

  const { userLoadedData } = useBurnState()

  const orders = useRemovalOrders()

  const userDepositedRemovalOrders = orders.filter((o) => o.userData.stakedBalance.isGreaterThan('0'))

  const allOrdersWithV2Pairs = allV2Pairs
    .map(([, p]) => p)
    .filter((p) =>
      userDepositedRemovalOrders.some((x) => x.lpAddress.toLowerCase() === p?.liquidityToken.address.toLowerCase()),
    )
    .map((p) => {
      const order = userDepositedRemovalOrders.find(
        (udro) => udro.lpAddress.toLowerCase() === p.liquidityToken.address.toLowerCase(),
      )
      return {
        type: 'orders' as LiquidityCardType,
        pair: p,
        ...order,
        lpTokenValue: order.userData.stakedBalance.times(
          getLpUsdcPrice(farms.find((f) => getAddress(f.lpAddresses) === p.liquidityToken.address)),
        ),
      }
    })

  const redemptions = data?.userPairRedemptions.map((r) => {
    const lpAddress = allV2Pairs
      .map(([, pair]) => pair)
      .find((p) => p?.liquidityToken.address.toLowerCase() === r.pair.id.toLowerCase())?.liquidityToken.address
    return {
      type: 'history' as LiquidityCardType,
      pair: allV2Pairs
        .map(([, pair]) => pair)
        .find((p) => p?.liquidityToken.address.toLowerCase() === r.pair.id.toLowerCase()),
      lpAddress,
      redemption: r,
      lpTokenValue: new BigNumber(r.liquidity).times(
        getLpUsdcPrice(farms.find((f) => getAddress(f.lpAddresses) === lpAddress)),
      ),
    }
  })

  const combinedList: {
    lpAddress: string
    type: LiquidityCardType
    pair: Pair
    userData?: DeserializedRemovalOrderUserData
    redemption?: PairRedemptionResponse
    lpTokenValue?: BigNumber
  }[] = allOrdersWithV2Pairs.concat(allV2PairsWithLiquidity).concat(redemptions)

  const { onWithdraw } = useClpHandlers()

  const pendingText = useMemo(
    () =>
      t('Cancel %token0%/%token1% removal order', {
        token0: currentPair ? unwrappedToken(currentPair.token0).symbol : '',
        token1: currentPair ? unwrappedToken(currentPair.token1).symbol : '',
      }),
    [currentPair, t],
  )

  const [onPresentCancelModal, onDismiss] = useModal(
    <TransactionConfirmationModal
      title=""
      attemptingTxn={attemptingTxn}
      hash={txHash || ''}
      content={() => <></>}
      pendingText={pendingText}
    />,
    true,
    true,
    'cancelRemovalOrderModal',
  )

  const addTransaction = useTransactionAdder()
  const handleCancel = useCallback(
    async (amount: string, p: Pair) => {
      setCurrentPair(p)
      onPresentCancelModal()
      try {
        const withdrawRes = await onWithdraw(p.liquidityToken.address, amount)
        setTxHash(withdrawRes.hash)

        addTransaction(withdrawRes, {
          summary: t('Your %token0Symbol%/%token1Symbol% removal order has been cancelled', {
            token0Symbol: p.token0.symbol,
            token1Symbol: p.token1.symbol,
          }),
        })
      } catch (err: any) {
        if (err?.code !== 4001) {
          console.error(err)
          toastError(t('Transaction Failed'), t('Please try again later'))
        }
      } finally {
        onDismiss()
      }
    },
    [addTransaction, onDismiss, onPresentCancelModal, onWithdraw, t, toastError],
  )

  const handleOptionChange = (option: OptionProps): void => {
    setFilter(option.value)
  }

  const totalSupply = useTotalSupply(currentPair?.liquidityToken)
  const pooledToken0 =
    totalSupply &&
    currentOrder &&
    currentPair?.getLiquidityValue(
      currentPair.token0,
      totalSupply,
      new TokenAmount(currentPair.liquidityToken, currentOrder?.userData.stakedBalance.toString()),
      false,
    )
  const pooledToken1 =
    totalSupply &&
    currentOrder &&
    currentPair?.getLiquidityValue(
      currentPair.token1,
      totalSupply,
      new TokenAmount(currentPair.liquidityToken, currentOrder?.userData.stakedBalance.toString()),
      false,
    )

  const onBeforeNavigate = (e, pair: Pair, order: DeserializedRemovalOrder) => {
    if (order) {
      e.preventDefault()
      setCurrentOrder(order)
      setCurrentPair(pair)
      onPresentDuplicatedOrderWarningModal()
    }
  }

  const handleCancelSelected = useCallback(async () => {
    setAttemptingModalCancelTxn(true)
    onPresentCancelModal()
    try {
      const withdrawRes = await onWithdraw(
        currentPair.liquidityToken.address,
        new TokenAmount(currentPair.liquidityToken, currentOrder.userData.stakedBalance.toString()).toFixed(
          currentPair.liquidityToken.decimals,
        ),
      )
      setTxHash(withdrawRes.hash)

      addTransaction(withdrawRes, {
        summary: t('Your %token0Symbol%/%token1Symbol% removal order has been cancelled', {
          token0Symbol: currentPair.token0.symbol,
          token1Symbol: currentPair.token1.symbol,
        }),
      })
    } catch (err: any) {
      if (err?.code !== 4001) {
        console.error(err)
        toastError(t('Transaction Failed'), t('Please try again later'))
      }
    } finally {
      onDismiss()
      setAttemptingModalCancelTxn(false)
    }
  }, [addTransaction, currentOrder, currentPair, onDismiss, onPresentCancelModal, onWithdraw, t, toastError])

  const handleCustomOnDismiss = useCallback(() => {
    if (txHash) {
      history.push(`/remove/smart/${currentPair.token0}/${currentPair.token1}`)
    }
    setAttemptingModalCancelTxn(false)
  }, [currentPair, history, txHash])

  const [onPresentDuplicatedOrderWarningModal] = useModal(
    <DuplicatedOrderWarningModal
      onConfirm={handleCancelSelected}
      customOnDismiss={handleCustomOnDismiss}
      attemptingTxn={attemptingModalCancelTxn}
      txHash={txHash}
      pooledToken0={pooledToken0}
      pooledToken1={pooledToken1}
      order={currentOrder}
      pair={currentPair}
    />,
    false,
    true,
    'duplicated-order-modal',
  )

  const renderBody = () => {
    if (!account) {
      return (
        <Text color="textSubtle" textAlign="center">
          {t('Connect to a wallet to view your liquidity.')}
        </Text>
      )
    }
    if (v2IsLoading || loading || !userLoadedData) {
      return [0, 1, 2].map((el) => <SkeletonCard key={el} />)
    }
    if (
      combinedList.filter(
        (c) => c && ((filter === 'all' && (c.type === 'orders' || c.type === 'supplied')) || filter === c.type),
      ).length > 0
    ) {
      return combinedList
        .filter((c) => c && ((filter === 'all' && (c.type === 'orders' || c.type === 'supplied')) || filter === c.type))
        .sort((prev, next) => {
          if (prev.type === next.type) {
            if (prev.type === 'history') {
              return parseInt(next.redemption.timestamp) - parseInt(prev.redemption.timestamp)
            }
            return next.lpTokenValue.minus(prev.lpTokenValue).toNumber()
          }
          switch (prev.type) {
            case 'supplied':
              return -1
            case 'history':
              return 1
            default:
              return 0
          }
        })
        .map((owp, i) => {
          if (!owp || !owp.pair || !owp.pair.liquidityToken) return <React.Fragment key={i.toString()} />

          const CardComponent = cardComponents[owp.type]
          return (
            <CardComponent
              key={
                owp.type !== 'history'
                  ? `${owp.lpAddress}-${owp.type}`
                  : `${owp.lpAddress}-${owp.type}-${owp.redemption.timestamp}`
              }
              pair={owp.pair}
              userData={owp.userData}
              account={account}
              redemption={owp.redemption}
              onCancel={handleCancel}
              onBeforeNavigate={onBeforeNavigate}
              farms={farms}
            />
          )
        })
    }
    return (
      <Text color="textSubtle" textAlign="center">
        {t('No records. If your liquidity is staked in Farms, it will not appear here but in the My Farms tab.')}
      </Text>
    )
  }

  return (
    <Box>
      {account && (
        <Select
          title=""
          style={isMobile ? { width: 150 } : { width: 150, marginLeft: '5rem' }}
          options={[
            {
              label: t('Holding'),
              value: 'all',
            },
            {
              label: t('Supplied'),
              value: 'supplied',
            },
            {
              label: t('Removal Orders'),
              value: 'orders',
            },
            {
              label: t('Removed'),
              value: 'history',
            },
          ]}
          onOptionChange={handleOptionChange}
        />
      )}
      <CardLayout style={{ marginTop: 30 }}>{renderBody()}</CardLayout>
    </Box>
  )
}
